import { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import EditIcon from '@/images/EditIcon.svg?react';
import SaveIcon from '@/images/SaveIcon.svg?react';
import CloseIcon from '@/images/CloseIconNew.svg?react';
import { formatDate, formatPayloadDate } from '@/helpers/dateHelpers/dateHelpers';
import { editTransactionValidationSchema } from './const/editTransactionValidationSchema';
import { TransactionType } from '@/types/wireTypes';
import { Loader } from '@/components/Loader/Loader';
import { FormikDatePicker } from '@/components/form/fields/FormikDatePicker/FormikDatePicker';
import { parseISO } from 'date-fns';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { IconButton } from '@/components/IconButton/IconButton';
import { Box } from '@/components/Box/Box';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { PrimaryDetailsPanel } from '@/bundle/shared/components/PrimaryDetailsPanel/PrimaryDetailsPanel';
import { PrimaryDetailsPanelHeader } from '@/bundle/shared/components/PrimaryDetailsPanel/styles';
import { PrimaryDetailsPanelColumn } from '@/bundle/shared/components/PrimaryDetailsPanel/PrimaryDetailsPanelColumn';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { updateTransactionDetails } from '../../api/api';

const BACKGROUND_VARIANT = 'linear-gradient(90.49deg, #eefffa 0%, #f1f5ff 52.67%, #fff4f9 100%);';

type UpdatedTransactionType = Pick<TransactionType, 'name' | 'expected_close_date'>;
type UpdateTransactionValuesType = { name: string; expected_close_date: Date };

type TransactionDetailsType = {
  transaction: TransactionType;
};

export const TransactionDetails = ({ transaction }: TransactionDetailsType) => {
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [isEdit, setIsEdit] = useState(false);

  const {
    mutate,
    data: updatedTransactionDetails,
    isPending,
  } = useMutation({
    mutationKey: ['update_transaction'],
    mutationFn: (values: UpdatedTransactionType) => {
      return updateTransactionDetails(id, values);
    },
    onSuccess(response) {
      if (response.error) {
        return showSnackbarResponseError(response?.error);
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.EditTransactionSuccess);
      queryClient.setQueryData(['load_transaction_details', id], response);
      setIsEdit(false);
    },
  });

  const { name, date_created, expected_close_date, ref_id, created_by } = transaction;

  const expectedCloseDate = expected_close_date ? parseISO(expected_close_date) : null;
  const formattedDateCreated = formatDate(date_created);
  const formattedExpectedCloseDate = formatDate(expected_close_date);

  const formik = useFormik({
    validationSchema: editTransactionValidationSchema,
    initialValues: { name, expected_close_date: expectedCloseDate },
    enableReinitialize: true,
    onSubmit: (values: UpdateTransactionValuesType) => {
      const payload = {
        name: values.name,
        expected_close_date: values.expected_close_date ? formatPayloadDate(values.expected_close_date) : null,
      };

      mutate(payload);
    },
  });

  const closeEdit = () => {
    formik.resetForm();
    setIsEdit(false);
  };

  const isEditHandler = () => {
    amplitudeService.logEvent(AMPLITUDE_EVENTS.EditTransactionRedirect);
    setIsEdit(true);
  };

  return (
    <FormikForm value={formik}>
      <PrimaryDetailsPanel
        variant={BACKGROUND_VARIANT}
        headerSlot={
          <>
            {isEdit ? (
              <FormikInput
                autoFocus
                name='name'
                disabled={!isEdit}
                variant='small'
                apiError={updatedTransactionDetails?.error}
              />
            ) : (
              <PrimaryDetailsPanelHeader>{name}</PrimaryDetailsPanelHeader>
            )}

            {isEdit && !isPending && (
              <Box display='flex' columnGap='12px'>
                <IconButton type='submit'>
                  <SaveIcon />
                </IconButton>

                <IconButton onClick={closeEdit} svgPath='stroke'>
                  <CloseIcon />
                </IconButton>
              </Box>
            )}
            {isPending && <Loader width={40} backgroundColor='transparent' />}
            {!isEdit && (
              <IconButton onClick={isEditHandler}>
                <EditIcon />
              </IconButton>
            )}
          </>
        }
      >
        <PrimaryDetailsPanelColumn name='Transaction ID' ratio='1'>
          {ref_id}
        </PrimaryDetailsPanelColumn>
        <PrimaryDetailsPanelColumn name='Creation Date' ratio='1'>
          {formattedDateCreated}
        </PrimaryDetailsPanelColumn>
        <PrimaryDetailsPanelColumn name='Expected Closing Date' ratio='1'>
          {isEdit ? (
            <FormikDatePicker name='expected_close_date' placeholder='Select Date' variant='small' />
          ) : (
            <>{formattedExpectedCloseDate}</>
          )}
        </PrimaryDetailsPanelColumn>
        <PrimaryDetailsPanelColumn name='Created By' ratio='1'>
          {created_by?.email || '-'}
        </PrimaryDetailsPanelColumn>
      </PrimaryDetailsPanel>
    </FormikForm>
  );
};
